import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateMeterKey } from "@/api/districts/mutations";
import { useApartments, useDistricts } from "@/api/districts/queries";
import type { Apartment, District } from "@/api/districts/types";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDistrict } from "@/contexts/district-context";
import { SearchableSelect } from "./searchable-select";

export function CreateKeyDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { t } = useTranslation();
	const { districtId: globalDistrictId } = useDistrict();
	const { data: districts = [] } = useDistricts();

	const [selectedDistrict, setSelectedDistrict] = useState<District | null>(
		null,
	);
	const [selectedApartment, setSelectedApartment] = useState<Apartment | null>(
		null,
	);
	const [email, setEmail] = useState("");
	const create = useCreateMeterKey();

	const activeDistrictId = selectedDistrict?.id ?? globalDistrictId;

	const { data: apartmentsData, isLoading: apartmentsLoading } = useApartments({
		district_id: activeDistrictId ?? undefined,
		page_size: 200,
	});
	const apartments = apartmentsData?.results ?? [];

	const initialDistrict =
		selectedDistrict ??
		districts.find((d) => d.id === globalDistrictId) ??
		null;

	useEffect(() => {
		if (open) {
			setSelectedDistrict(null);
			setSelectedApartment(null);
			setEmail("");
		}
	}, [open]);

	function handleDistrictChange(district: District) {
		setSelectedDistrict(district);
		setSelectedApartment(null);
	}

	function handleSubmit() {
		if (!selectedApartment) return;
		create.mutate(
			{ apartment: selectedApartment.id, email: email.trim() || null },
			{
				onSuccess: () => {
					toast.success(
						email.trim()
							? t("meterKeys.createdAndSent")
							: t("meterKeys.created"),
					);
					onOpenChange(false);
				},
				onError: () => toast.error(t("meterKeys.createError")),
			},
		);
	}

	const canSubmit = selectedApartment !== null && !create.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{t("meterKeys.createTitle")}</DialogTitle>
					<DialogDescription>
						{t("meterKeys.createDescription")}
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-4 py-2">
					<div className="flex flex-col gap-1.5">
						<Label>{t("meterKeys.district")}</Label>
						<SearchableSelect
							items={districts}
							selected={initialDistrict}
							onSelect={handleDistrictChange}
							placeholder={t("meterKeys.selectDistrict")}
							searchPlaceholder={t("meterKeys.searchDistrict")}
							emptyMessage={t("meterKeys.noDistricts")}
							getLabel={(d) => d.name}
						/>
					</div>
					<div className="flex flex-col gap-1.5">
						<Label>{t("meterKeys.apartment")}</Label>
						{activeDistrictId ? (
							<SearchableSelect
								items={apartments}
								selected={selectedApartment}
								onSelect={setSelectedApartment}
								disabled={apartmentsLoading}
								placeholder={
									apartmentsLoading
										? t("meterKeys.loadingApartments")
										: t("meterKeys.apartmentSearchPlaceholder")
								}
								searchPlaceholder={t("meterKeys.apartmentSearchPlaceholder")}
								emptyMessage={t("meterKeys.noApartments")}
								getLabel={(a) => `${a.street} ${a.apartment_number}`}
							/>
						) : (
							<p className="text-sm text-muted-foreground">
								{t("meterKeys.selectDistrictFirst")}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1.5">
						<Label htmlFor="key-email">
							{t("meterKeys.userEmail")}{" "}
							<span className="text-muted-foreground font-normal">
								({t("meterKeys.optional")})
							</span>
						</Label>
						<Input
							id="key-email"
							type="email"
							autoComplete="email"
							name="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							placeholder={t("meterKeys.emailPlaceholder")}
							onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
						/>
					</div>
				</div>
				<DialogFooter>
					<Button
						size="sm"
						variant="outline"
						onClick={() => onOpenChange(false)}
						disabled={create.isPending}
					>
						{t("meterKeys.cancel")}
					</Button>
					<Button onClick={handleSubmit} disabled={!canSubmit}>
						{create.isPending ? t("meterKeys.creating") : t("meterKeys.create")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
